import {
  Currency,
  SUPPORTED_CURRENCIES,
  DEFAULT_CURRENCY,
} from "../../../shared/types/index.js";

/**
 * Get a currency object by its ISO code.
 * Returns DEFAULT_CURRENCY (EUR) if code is not provided or not found.
 *
 * @param code - ISO 4217 currency code (e.g., 'EUR', 'USD')
 * @returns Currency object with code, symbol, name, and locale
 */
export function getCurrencyByCode(code?: string): Currency {
  if (!code) {
    return DEFAULT_CURRENCY;
  }

  const currency = SUPPORTED_CURRENCIES.find(
    (c) => c.code.toLowerCase() === code.toLowerCase()
  );

  return currency || DEFAULT_CURRENCY;
}

/**
 * Format a numeric amount as currency with proper locale formatting.
 *
 * @param amount - Numeric amount to format
 * @param currencyCode - ISO 4217 currency code (defaults to EUR)
 * @param locale - Optional locale override (uses currency's default locale if not provided)
 * @returns Formatted currency string (e.g., "1.234,56 €")
 *
 * @example
 * formatCurrency(1234.56, 'EUR') // "1.234,56 €"
 * formatCurrency(1234.56, 'USD') // "$1,234.56"
 */
export function formatCurrency(
  amount: number,
  currencyCode?: string,
  locale?: string
): string {
  const currency = getCurrencyByCode(currencyCode);
  const effectiveLocale = locale || currency.locale;

  return new Intl.NumberFormat(effectiveLocale, {
    style: "currency",
    currency: currency.code,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/**
 * Format an hourly rate with currency symbol.
 * Uses the same formatting as formatCurrency but with "/h" suffix.
 *
 * @param rate - Hourly rate to format
 * @param currencyCode - ISO 4217 currency code (defaults to EUR)
 * @param locale - Optional locale override (uses currency's default locale if not provided)
 * @returns Formatted hourly rate string (e.g., "75,00 €/h")
 *
 * @example
 * formatHourlyRate(75, 'EUR') // "75,00 €/h"
 * formatHourlyRate(100, 'USD') // "$100.00/h"
 */
export function formatHourlyRate(
  rate: number,
  currencyCode?: string,
  locale?: string
): string {
  const formattedAmount = formatCurrency(rate, currencyCode, locale);
  return `${formattedAmount}/h`;
}

/**
 * Get currency options for dropdown selectors.
 * Returns an array of objects with value (ISO code) and label (code + name).
 *
 * @returns Array of dropdown options
 *
 * @example
 * getCurrencyOptions() // [
 *   { value: 'EUR', label: 'EUR - Euro' },
 *   { value: 'USD', label: 'USD - US Dollar' },
 *   ...
 * ]
 */
export function getCurrencyOptions(): Array<{ value: string; label: string }> {
  return SUPPORTED_CURRENCIES.map((currency) => ({
    value: currency.code,
    label: `${currency.code} - ${currency.name}`,
  }));
}

/**
 * Get the currency symbol for a given currency code.
 *
 * @param currencyCode - ISO 4217 currency code (defaults to EUR)
 * @returns Currency symbol (e.g., '€', '$')
 *
 * @example
 * getCurrencySymbol('EUR') // '€'
 * getCurrencySymbol('USD') // '$'
 */
export function getCurrencySymbol(currencyCode?: string): string {
  const currency = getCurrencyByCode(currencyCode);
  return currency.symbol;
}
